import * as AuthCustomerActivity from './activity/AuthCustomerActivity.selectors';
import * as AuthCustomerConsentConfigs from './consentConfigs/AuthCustomerConsentConfigs.selectors';
import * as AuthCustomerDSAR from './dsar/AuthCustomerDSAR.selectors';
import * as AuthCustomerUsers from './users/AuthCustomerUsers.selectors';

import { DOMAIN } from './AuthCustomer.constants';
import castArray from 'lodash/castArray';
import { denormalize } from 'normalizr';
import get from 'lodash/get';
import { getCustomerPlan as getCustomerPlanById } from '../../../common/state/db/customer/Customer.selectors';
import { getStore as getDB } from '../../../common/state/db/DB.store';
import { isAllowedTo } from '../../../common/helpers/Permissions';
import schema from '../../../common/state/db/customer/Customer.schema';

export const activity = AuthCustomerActivity;
export const consentConfigs = AuthCustomerConsentConfigs;
export const dsar = AuthCustomerDSAR;
export const users = AuthCustomerUsers;

export const getStore = state => get(state, DOMAIN) || {};

export const getEntityById = (state, id) => denormalize(id, schema, getDB(state)) || {};
export const getEntitiesForIds = (state, ids) => denormalize(ids, [schema], getDB(state));

export const getError = state => getStore(state).error;
export const getId = state => getStore(state).id;
export const getSubscription = state => getStore(state).subscription;
/** @type {import('react-redux').Selector<Object,string>} */
export const getShortCode = state => getStore(state).shortCode || getId(state);
export const getBeaconId = state => getStore(state).beaconId;
export const getOrganizations = state =>
    get(getEntityById(state, getId(state)), 'organizations') || [];
export const getPermissions = state => getStore(state).permissions;
/** Returns customer plan from the shared DB customer selector. */
export const getCustomerPlan = state => getCustomerPlanById(state, getId(state));
/** Returns whether plan permissions include the requested content type. */
const getAllowedByPlan = (state, { toPerform: action, on: contentType }) =>
    get(getPermissions(state), ['allow', action], []).includes(contentType);
export const getCodeSplittingAllowedByPlan = state =>
    getAllowedByPlan(state, { toPerform: 'write', on: 'cmp_code_splitting' });
export const getDsarAutomationAllowed = state =>
    getAllowedByPlan(state, { toPerform: 'write', on: 'dsar_automation' });
export const getRoles = state => getEntityById(state, getId(state)).roles || [];
export const getRootOrg = state =>
    getEntityById(state, getId(state)).organizations.find(({ isRoot }) => isRoot);
export const getRootOrgId = state => get(getRootOrg(state), 'orgId');
export const isLoading = state => getStore(state).loading || false;
export const canChat = state => getStore(state).canChat;
export const canEmail = state => getStore(state).canEmail;

export const hasPermission = (state, { toPerform: action, on: contentType, onAny: anyType }) => {
    const customer = getStore(state);
    if (anyType) {
        return castArray(anyType).some(type =>
            isAllowedTo([], customer, { [type]: { [action]: true } }, getPermissions(state))
        );
    }
    return isAllowedTo([], customer, { [contentType]: { [action]: true } }, getPermissions(state));
};

/** Returns the first boolean entitlement found for the provided plan keys. */
const getCustomerPlanBoolean = (state, keys = []) => {
    const customerPlan = getCustomerPlan(state);
    for (const key of keys) {
        const value = customerPlan[key];
        if (typeof value === 'boolean') {
            return value;
        }
    }
    return false;
};

/** Returns whether DSAR Consumer SSO is visible by plan entitlement. */
export const getCanViewDsarConsumerSso = state =>
    getCustomerPlanBoolean(state, ['data_mapping', 'dataMapping']) ||
    getCustomerPlanBoolean(state, ['data_discovery', 'dataDiscovery']);

/** Returns whether the AI Assistant feature is enabled by plan entitlement. */
export const getCanViewAiAssistant = state =>
    getCustomerPlanBoolean(state, ['privacyAi', 'privacy_ai']);

/** Returns whether basic DSAR pages are enabled in customer plan. */
export const getCanViewDsarBasic = state => getCustomerPlanBoolean(state, ['dsar']);

/** Returns whether the Basic DSAR workflow package is enabled by plan entitlement. */
export const getCanViewDsarWorkflowBasic = state =>
    getCustomerPlanBoolean(state, ['dsarWorkflowBasic', 'dsar_workflow_basic']);

/** Returns whether the Pro DSAR workflow package is enabled by plan entitlement. */
export const getCanViewDsarWorkflowPro = state =>
    getCustomerPlanBoolean(state, ['dsarWorkflowPro', 'dsar_workflow_pro']);

/** Returns whether either DSAR workflow package is enabled by plan entitlement. */
export const getCanViewDsarWorkflows = state =>
    getCanViewDsarWorkflowBasic(state) || getCanViewDsarWorkflowPro(state);

/** Returns whether the customer gets the Basic workflow experience. Pro wins when both are on. */
export const getIsBasicDsarWorkflow = state =>
    getCanViewDsarWorkflowBasic(state) && !getCanViewDsarWorkflowPro(state);

/** Returns whether the notification integrations (Slack, Teams) are enabled by plan entitlement. */
export const getCanUseChatIntegration = state =>
    getCustomerPlanBoolean(state, ['chatIntegration', 'chat_integration']);

/** Returns whether the CMP banner "Powered by Osano" logo is enabled by plan entitlement. */
export const getCmpBannerLogoEnabled = state =>
    getCustomerPlanBoolean(state, ['cmpBannerLogo', 'cmp_banner_logo']);

/**
 * Whether this customer is pinned to the Classic CMP theme (CMP-3138).
 *
 * Defaults to `true` — the `cmp_classic` column does not exist yet, and Classic is the safe
 * preselect until it does. Not `getCustomerPlanBoolean`: its `false` default would read as Modern.
 */
export const getCmpClassic = state => {
    const customerPlan = getCustomerPlan(state);
    for (const key of ['cmpClassic', 'cmp_classic']) {
        if (typeof customerPlan[`${key}`] === 'boolean') {
            return customerPlan[`${key}`];
        }
    }
    return true;
};

/** Returns whether the CMP "analytics on by default" setting is enabled by plan entitlement. */
export const getCmpAnalyticsOnByDefaultEnabled = state =>
    getCustomerPlanBoolean(state, ['cmpAnalyticsOnByDefault', 'cmp_analytics_on_by_default']);

/** Returns whether CMP US jurisdiction customization is enabled by plan entitlement. */
export const getCmpUsJurisdictionCustomizationEnabled = state =>
    getCustomerPlanBoolean(state, [
        'cmpUsJurisdictionCustomization',
        'cmp_us_jurisdiction_customization',
    ]);
